/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions,no-unused-expressions */
import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Key} from 'ts-key-enum';

import {
  FrontChangeEvent,
  FrontKeyboardEvent,
  Handler,
  HandlerOf,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {padStart} from '../../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {DDHHMM, DelayGranularity} from '../../../editors/commons/paramModel';
import {RulesGenericInput, StyledDigitsInput, StyledNumberLabel} from './genericInput';

/*
 * Types.
 */

interface InputDDHHMMProps {
  granularity: DelayGranularity;
  dayHHMM?: DDHHMM;
  error?: string;
  isDisabled?: boolean;
  setDayHHMM: HandlerOf<DDHHMM>;
}

/*
 * Constants.
 */

const DayUpper = 100; // Upper bound (excluded) for days.
const HHUpper = 100; // Upper bound (excluded) for hours.
const MMUpper = 60; // Upper bound (excluded) for minutes.

/*
 * Component.
 */

// @RULES TODO: consider refactoring this component
export const InputDDHHMM: FC<InputDDHHMMProps> = (props) => {
  const {granularity, dayHHMM, isDisabled, setDayHHMM, error} = props;
  const dayRef = useRef<HTMLInputElement>(null);
  const hhRef = useRef<HTMLInputElement>(null);
  const mmRef = useRef<HTMLInputElement>(null);
  const [days, setDays] = useState<string>(computePaddedDateNumber(dayHHMM?.days));
  const [hh, setHH] = useState<string>(computePaddedDateNumber(dayHHMM?.hh));
  const [mm, setMM] = useState<string>(computePaddedDateNumber(dayHHMM?.mm));

  useEffect(() => {
    setDays(computePaddedDateNumber(dayHHMM?.days));
    setHH(computePaddedDateNumber(dayHHMM?.hh));
    setMM(computePaddedDateNumber(dayHHMM?.mm));
  }, [dayHHMM]);

  const onChangeDays = useMemo(() => updateValue(setDays, DayUpper, () => hhRef.current?.focus()), [hhRef]);
  const onChangeHH = useMemo(() => updateValue(setHH, HHUpper, () => mmRef.current?.focus()), [mmRef]);
  const onChangeMM = useMemo(() => updateValue(setMM, MMUpper), []);

  // Switch back to HH if we press 'delete' on an empty MM.
  const onMMKeyDown = useCallback(
    (evt: FrontKeyboardEvent<HTMLInputElement>) => {
      if (mm === '' && (evt.key === Key.Delete || evt.key === Key.Backspace)) {
        hhRef.current?.focus();
      }
    },
    [mm],
  );

  // Only externally update HHMM's value on blur.
  const setBlur = useCallback(() => {
    // We're reading from the dom because day/hh/mm can be stale if the blur is triggered from onChange.
    const numberDays = parseNumber(dayRef.current?.value || '');
    const numberHH = parseNumber(hhRef.current?.value || '');
    const numberMM = parseNumber(mmRef.current?.value || '');

    // Cosmetic update : update numbers to be 2-digits on blur.
    if (isDefined(numberDays) && numberDays < 10 && days.length < 2) {
      setDays(padStart(numberDays, 2, '0'));
    }

    if (isDefined(numberHH) && numberHH < 10 && hh.length < 2) {
      setHH(padStart(numberHH, 2, '0'));
    }

    if (isDefined(numberMM) && numberMM < 10 && mm.length < 2) {
      setMM(padStart(numberMM, 2, '0'));
    }

    // Propagate the actual update to our parent.
    setDayHHMM({
      days: numberDays,
      hh: numberHH,
      mm: numberMM,
    });
  }, [setDayHHMM, days, hh, mm]);

  return (
    <RulesGenericInput hasError={Boolean(error)} inputId="input-am-pm">
      {granularity === 'days' && (
        <>
          <StyledDigitsInput
            ref={dayRef}
            type="number"
            placeholder="00"
            value={days}
            onChange={onChangeDays}
            min={0}
            onBlur={setBlur}
            disabled={isDisabled}
          />
          <StyledNumberLabel>
            <Trans id="input-dd-hh-mm-d">d</Trans>
          </StyledNumberLabel>
        </>
      )}
      <StyledDigitsInput
        ref={hhRef}
        type="number"
        placeholder="00"
        value={hh}
        onChange={onChangeHH}
        min={0}
        onBlur={setBlur}
        disabled={isDisabled}
      />
      <StyledNumberLabel>
        <Trans id="input-dd-hh-mm-h">h</Trans>
      </StyledNumberLabel>
      <StyledDigitsInput
        ref={mmRef}
        type="number"
        placeholder="00"
        value={mm}
        onChange={onChangeMM}
        onKeyDown={onMMKeyDown}
        min={0}
        max={59}
        onBlur={setBlur}
        disabled={isDisabled}
      />
      <StyledNumberLabel>
        <Trans id="input-dd-hh-mm-m">m</Trans>
      </StyledNumberLabel>
    </RulesGenericInput>
  );
};

/*
 * Helpers.
 */

function parseNumber(str: string): number | undefined {
  if (str === '') {
    return undefined;
  }

  const number = Number(str);
  if (Number.isNaN(number)) {
    return undefined;
  }

  return number;
}

// Update a field value if it is legal (a number that respects the upper bound),
// and executes a side-effect after that.
function updateValue(setValue: HandlerOf<string>, upperBound: number, goToNextField?: Handler) {
  return (evt: FrontChangeEvent<HTMLInputElement>) => {
    const {value} = evt.target;
    if (value === '') {
      setValue('');
      return;
    }

    const maybeNumber = parseNumber(value);
    if (maybeNumber !== undefined && maybeNumber < upperBound) {
      // We switch to the next field when the previous value was single digit and the current one is double.
      const shouldSwitchToNextField = value.length >= 2;
      setValue(value);

      // When the day input is complete, set the focus to the HH input.
      if (shouldSwitchToNextField && goToNextField) {
        goToNextField();
      }
    }
  };
}

function computePaddedDateNumber(dateNumber?: number) {
  if (dateNumber === undefined) {
    return '';
  }

  return padStart(dateNumber, 2, '0');
}
